// Library Methods
import { useMemo } from "react";

// Components
import Box from "@mui/material/Box";
import LevelBand, { LevelEntryType } from "./LevelBand";
import { TrialEntryType } from "./TrialCard";
import { RUNGS } from "../SummarySection/StereoLadderChart";

// Types
import { ExamSectionReportType } from "src/services/exams/types";

type ModulesType = NonNullable<
  ExamSectionReportType["postProcessing"]["modules"]
>;
export type StereoLadderModuleType = ModulesType["StereoLadder"];

// How many levels, from the top of the ladder, sit side by side on one line
const ROW_LEVEL_COUNT = 3;

type TrialReviewSectionType = {
  stereoLadder: StereoLadderModuleType;
};

// Every trial the algorithm presents, in order. Newer reports say which rung each trial belongs to
// (levelNumber, 1-based into the ladder), catch trials included. Before that a catch trial carries
// no disparity of its own (0″), so it joins the band of whichever rung trial comes next -- the
// only rung a catch trial ever interrupts is the one either side of it.
const assignBands = (
  trials: Array<TrialEntryType>,
  ladder: Array<number>
): Array<TrialEntryType & { band: number }> => {
  const withBand: Array<TrialEntryType & { band: number }> = new Array(
    trials.length
  );
  let nextRungDisparity: number | null = null;
  for (let i = trials.length - 1; i >= 0; i -= 1) {
    const trial = trials[i];
    if (trial.kind !== "Catch") {
      nextRungDisparity = trial.disparityArcsec;
    }
    const levelRung =
      trial.levelNumber !== undefined
        ? ladder[trial.levelNumber - 1]
        : undefined;
    withBand[i] = {
      ...trial,
      band:
        levelRung ??
        (trial.kind !== "Catch"
          ? trial.disparityArcsec
          : nextRungDisparity ?? trial.disparityArcsec),
    };
  }
  return withBand;
};

// The audit trail behind the summary's threshold: every rung and catch trial the patient saw,
// grouped by disparity, with the picture they were shown and the one they picked.
const TrialReviewSection = ({ stereoLadder }: TrialReviewSectionType) => {
  const trials = stereoLadder?.trials;
  const ladderArcsec = stereoLadder?.ladderArcsec;
  const ladder = useMemo(
    () => (ladderArcsec && ladderArcsec.length > 0 ? ladderArcsec : RUNGS),
    [ladderArcsec]
  );
  const bands = useMemo(
    () => (trials ? assignBands(trials, ladder) : []),
    [trials, ladder]
  );

  const levelsByDisparity = useMemo(() => {
    const map = new Map<number, LevelEntryType>();
    (stereoLadder?.levels ?? []).forEach((level) => {
      map.set(level.disparityArcsec, level);
    });
    return map;
  }, [stereoLadder?.levels]);

  if (!stereoLadder || !trials || trials.length === 0) return null;

  const thresholdValue = stereoLadder.thresholdArcsec?.value ?? null;

  const renderLevelBand = (rung: number, index: number) => (
    <LevelBand
      key={rung}
      rung={rung}
      levelIndex={index + 1}
      level={levelsByDisparity.get(rung)}
      trials={bands.filter((trial) => trial.band === rung)}
      isThreshold={thresholdValue === rung}
    />
  );

  return (
    <Box display="flex" flexDirection="column" gap="8px">
      {/* The first levels hold a single trial each, so they share a line when it fits. Like the
          trial cards, each grows into whatever its line leaves over, and one that wraps onto a
          line by itself spans the whole width. */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
        {ladder.slice(0, ROW_LEVEL_COUNT).map((rung, index) => (
          <Box key={rung} sx={{ flex: "1 1 200px", minWidth: 0 }}>
            {renderLevelBand(rung, index)}
          </Box>
        ))}
      </Box>
      {ladder
        .slice(ROW_LEVEL_COUNT)
        .map((rung, index) => renderLevelBand(rung, index + ROW_LEVEL_COUNT))}
    </Box>
  );
};

export default TrialReviewSection;
